import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, RefreshCw, ChevronRight, Compass, Sparkles, CheckCircle2, Calendar } from 'lucide-react';
import { CHALLENGES, SUITE_META } from '@/data/landing2Data';
import { SuiteGlyph, SuiteChip } from './SuiteVisuals';

export default function ChallengeNavigator() {
  const { t } = useTranslation();
  const [chosenId, setChosenId] = useState(null);
  const chosen = CHALLENGES.find((c) => c.id === chosenId);
  const fp = (id, suffix) => t(`pages.landing.findPath.${id}.${suffix}`);

  return (
    <section id="desafios" className="bg-white border-y border-slate-100" data-testid="challenge-navigator">
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 border border-teal-100 px-4 py-1.5 text-xs font-semibold text-[#176A70]">
            <Compass className="h-3.5 w-3.5" /> {t('pages.landing2.challenge.eyebrow')}
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="challenge-heading">
            {t('pages.landing2.challenge.heading')}
          </h2>
          <p className="mt-4 text-slate-600 md:text-lg">{t('pages.landing2.challenge.sub')}</p>
        </div>

        {!chosen ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="challenge-grid">
            {CHALLENGES.map((c) => {
              const entry = SUITE_META[c.steps[0]];
              return (
                <button
                  key={c.id}
                  onClick={() => setChosenId(c.id)}
                  data-testid={`challenge-card-${c.id}`}
                  className="text-left group rounded-2xl p-6 bg-[#FDFBF7] border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
                  style={{ borderTop: `3px solid ${entry.color}` }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-bold leading-snug">{fp(c.id, 'title')}</h3>
                    <ArrowRight className="h-4 w-4 text-slate-400 shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" style={{ color: undefined }} />
                  </div>
                  <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">{fp(c.id, 'subtitle')}</p>
                  <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold" style={{ color: entry.color }}>
                    <SuiteGlyph sid={c.steps[0]} className="h-3.5 w-3.5" /> {entry.name} →
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="mt-10" data-testid={`challenge-detail-${chosen.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#176A70] mb-2">{t('pages.landing2.challenge.pathTitle')}</p>
                <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{fp(chosen.id, 'title')}</h3>
                <p className="mt-1.5 text-sm text-slate-500 max-w-2xl">{fp(chosen.id, 'subtitle')}</p>
              </div>
              <button
                onClick={() => setChosenId(null)}
                data-testid="challenge-reset"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:border-slate-500"
              >
                <RefreshCw className="h-3.5 w-3.5" /> {t('pages.landing2.challenge.reset')}
              </button>
            </div>

            {/* Colored suite-chip flow ribbon */}
            <div className="flex flex-wrap items-center gap-2 mb-8 p-5 rounded-2xl bg-[#FDFBF7] border border-slate-200" data-testid={`challenge-sequence-${chosen.id}`}>
              {chosen.steps.map((sid, idx) => (
                <React.Fragment key={sid + idx}>
                  {idx > 0 && <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                  <SuiteChip sid={sid} />
                </React.Fragment>
              ))}
            </div>

            {/* Step cards with suite accent */}
            <div className={`grid md:grid-cols-2 gap-4 ${chosen.steps.length >= 4 ? 'lg:grid-cols-4' : chosen.steps.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
              {chosen.steps.map((sid, idx) => {
                const n = idx + 1;
                const meta = SUITE_META[sid];
                const isFirst = idx === 0;
                const isLast = idx === chosen.steps.length - 1;
                return (
                  <div
                    key={n}
                    className="relative rounded-2xl bg-white border border-slate-200 p-5 shadow-sm"
                    style={{ borderLeft: `4px solid ${meta.color}` }}
                    data-testid={`challenge-step-${chosen.id}-${n}`}
                  >
                    {(isFirst || isLast) && (
                      <span
                        className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold text-white"
                        style={{ background: isFirst ? meta.color : '#7C3AED' }}
                      >
                        {isFirst ? <><Sparkles className="h-3 w-3" /> {t('pages.landing2.challenge.start')}</>
                                 : <><CheckCircle2 className="h-3 w-3" /> {t('pages.landing2.challenge.outcome')}</>}
                      </span>
                    )}
                    <p className="text-xs uppercase tracking-wider font-bold mt-1.5" style={{ color: meta.color }}>
                      {t('pages.landing2.challenge.step')} {n}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <SuiteGlyph sid={sid} className="h-4 w-4" />
                      <h4 className="font-bold">{meta.name}</h4>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed mt-2" data-testid={`challenge-why-${chosen.id}-${n}`}>
                      {fp(chosen.id, `step${n}.why`)}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 rounded-2xl text-white" style={{ background: 'linear-gradient(105deg,#0F4A4E,#176A70)' }} data-testid={`challenge-cta-${chosen.id}`}>
              <div>
                <p className="text-xl font-bold tracking-tight">{t('pages.landing2.challenge.ctaTitle')}</p>
                <p className="text-sm text-teal-100 mt-1">{t('pages.landing2.challenge.ctaSub')}</p>
              </div>
              <a href="#contact" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F4A4E] px-6 py-3 text-sm font-bold shrink-0 hover:opacity-90" data-testid="challenge-cta-btn">
                <Calendar className="h-4 w-4" /> {t('pages.landing2.challenge.ctaBtn')}
              </a>
            </div>
          </div>
        )}

        <p className="mt-10 text-sm text-slate-400 italic leading-relaxed max-w-3xl" data-testid="challenge-closing">
          {t('pages.landing2.challenge.closing')}
        </p>
      </div>
    </section>
  );
}
